// Inventory API DataSource

import {Http} from '../../../Data/config/Http';
import {API_ROUTES, ApiUtils} from '../../../constants/apiRoutes';
import type {BaseResponse, Pagination} from '../../../Data/types/BaseResponse';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

import {
  InventoryItemDTO,
  LocationDTO,
  TrackedSerialDTO,
  SerialDetailsDTO,
  SerialTimelineResponseDTO,
  InventoryMovementDTO,
  ProductInventoryStatusDTO,
  CreateStockRequestDTO,
  UpdateInventoryRequestDTO,
  InventoryMovementRequestDTO,
  InventoryFiltersDTO,
  InventoryMovementFiltersDTO,
  InventoryStatisticsDTO,
  SerialStatisticsDTO
} from '../models/inventory.dto';

/** Summary block some inventory report endpoints attach to `pagination`. */
export interface InventoryReportSummary {
  total_quantity?: number;
  low_stock_products?: number;
  out_of_stock_products?: number;
}

/** Pagination for the comprehensive report (adds an optional `summary`). */
export interface InventoryReportPagination extends Pagination {
  summary?: InventoryReportSummary;
}

/** Object shape of the comprehensive-report response (it can also be a bare array). */
export interface InventoryComprehensiveReport {
  items?: unknown[];
  data?: unknown[];
  pagination?: InventoryReportPagination;
}

export class InventoryAPIDataSource {

  // Static guard to prevent multiple simultaneous requests to statistics endpoint
  private static isStatisticsRequestInProgress = false;
  private static statisticsRequestCount = 0;
  private static lastStatisticsRequest: Date | null = null;

  // Inventory Items
  static async getAllInventoryItems (filters?: InventoryFiltersDTO): Promise<InventoryItemDTO[]> {
    try {
      // Build query parameters from filters
      const queryParams: Record<string, string | number | boolean> = {};
      if (filters) {
        if (filters.product_id) {queryParams.product_id = filters.product_id;}
        if (filters.location_id) {queryParams.location_id = filters.location_id;}
        if (filters.low_stock_only) {queryParams.low_stock_only = filters.low_stock_only;}
        if (filters.page) {queryParams.page = filters.page;}
        if (filters.limit !== undefined) {queryParams.limit = filters.limit;}
      }

      const url = ApiUtils.buildUrl(API_ROUTES.INVENTORY.BASE, queryParams);

      const response = await Http.get<BaseResponse<InventoryItemDTO[]>>(url);
      return unwrapResponse(response.data, 'فشل في جلب عناصر المخزون');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getInventoryItemById (id: string): Promise<InventoryItemDTO> {
    try {
      const response = await Http.get<BaseResponse<InventoryItemDTO>>(API_ROUTES.INVENTORY.BY_ID(id));
      return unwrapResponse(response.data, 'فشل في جلب عنصر المخزون');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async addStock (stockData: CreateStockRequestDTO): Promise<InventoryItemDTO> {
    try {
      const response = await Http.post<BaseResponse<InventoryItemDTO>>(API_ROUTES.INVENTORY.ADD_STOCK, stockData);
      return unwrapResponse(response.data, 'فشل في إضافة المخزون', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async updateInventoryItem (id: string, updateData: UpdateInventoryRequestDTO): Promise<InventoryItemDTO> {
    try {
      const response = await Http.put<BaseResponse<InventoryItemDTO>>(API_ROUTES.INVENTORY.BY_ID(id), updateData);
      return unwrapResponse(response.data, 'فشل في تحديث عنصر المخزون');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Locations API removed per backend deprecation
  static async getAllLocations (): Promise<LocationDTO[]> {
    return [];
  }

  static async getTrackedSerials (filters?: {
    product_id?: string;
    location_id?: string;
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ data: TrackedSerialDTO[]; pagination?: Pagination }> {
    try {
      const queryParams: Record<string, string | number | boolean> = {};
      if (filters) {
        if (filters.product_id) {queryParams.product_id = filters.product_id;}
        if (filters.location_id) {queryParams.location_id = filters.location_id;}
        if (filters.status) {queryParams.status = filters.status;}
        if (filters.page) {queryParams.page = filters.page;}
        if (filters.limit !== undefined) {queryParams.limit = filters.limit;}
        if (filters.search) {queryParams.search = filters.search;}
      }

      const url = ApiUtils.buildUrl(API_ROUTES.SERIALS.BASE, queryParams);

      const response = await Http.get<BaseResponse<TrackedSerialDTO[]>>(url);
      const raw = response.data;
      const items = unwrapResponse(raw, 'فشل في جلب الأرقام التسلسلية المتتبعة');
      return {
        data: items,
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getSerialDetails (serialNumber: string): Promise<SerialDetailsDTO> {
    try {
      const response = await Http.get<BaseResponse<SerialDetailsDTO>>(
        API_ROUTES.INVENTORY.SERIAL_DETAILS(serialNumber)
      );
      const data = unwrapResponse(response.data, 'فشل في جلب تفاصيل السيريال');
      return data && typeof data === 'object' ? data : {
        serial_number: serialNumber,
        product_name: '',
        status: '',
        location_name: '',
        created_at: ''
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async deleteSerial (id: string): Promise<void> {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.SERIALS.BY_ID(id));
      unwrapResponse(response.data, 'فشل في حذف الرقم التسلسلي', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getSerialTimeline (serialNumber: string, page = 1, limit = 50): Promise<SerialTimelineResponseDTO> {
    try {
      const url = ApiUtils.buildUrl(API_ROUTES.INVENTORY.SERIAL_TIMELINE(serialNumber), {page, limit});
      const response = await Http.get<BaseResponse<SerialTimelineResponseDTO>>(url);
      const raw = response.data;
      const data = unwrapResponse(raw, 'فشل في جلب الخط الزمني للسيريال');
      // Backend may return array directly or { timeline, ... }
      if (Array.isArray(data)) {
        return {timeline: data};
      }
      if (data && typeof data === 'object' && 'timeline' in data) {
        return data as SerialTimelineResponseDTO;
      }
      return {timeline: []};
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getInventoryMovements (filters?: InventoryMovementFiltersDTO): Promise<{ data: InventoryMovementDTO[]; pagination?: Pagination }> {
    try {
      // Build query parameters from filters
      const queryParams: Record<string, string | number | boolean> = {};
      if (filters) {
        if (filters.product_id) {queryParams.product_id = filters.product_id;}
        if (filters.location_id) {queryParams.location_id = filters.location_id;}
        if (filters.type) {queryParams.type = filters.type;}
        if (filters.date_from) {queryParams.date_from = filters.date_from;}
        if (filters.date_to) {queryParams.date_to = filters.date_to;}
        if (filters.page) {queryParams.page = filters.page;}
        if (filters.limit !== undefined) {queryParams.limit = filters.limit;}
      }

      const url = ApiUtils.buildUrl(API_ROUTES.INVENTORY.MOVEMENTS, queryParams);

      const response = await Http.get<BaseResponse<InventoryMovementDTO[]>>(url);
      const raw = response.data;
      const items = unwrapResponse(raw, 'فشل في جلب حركات المخزون');
      return {
        data: items,
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async createInventoryMovement (movementData: InventoryMovementRequestDTO): Promise<InventoryMovementDTO> {
    try {
      const response = await Http.post<BaseResponse<InventoryMovementDTO>>(API_ROUTES.INVENTORY.MOVEMENTS, movementData);
      return unwrapResponse(response.data, 'فشل في إنشاء حركة المخزون', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getProductInventoryStatus (productId?: string): Promise<ProductInventoryStatusDTO[]> {
    try {
      const url = productId
        ? `${API_ROUTES.INVENTORY.PRODUCT_INVENTORY_STATUS}?product_id=${productId}`
        : API_ROUTES.INVENTORY.PRODUCT_INVENTORY_STATUS;

      const response = await Http.get<BaseResponse<ProductInventoryStatusDTO[]>>(url);
      return unwrapResponse(response.data, 'فشل في جلب حالة مخزون المنتج');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getProductsInventoryStatus (): Promise<ProductInventoryStatusDTO[]> {
    try {
      const response = await Http.get<BaseResponse<ProductInventoryStatusDTO[]>>(API_ROUTES.INVENTORY.PRODUCTS_INVENTORY_STATUS);
      return unwrapResponse(response.data, 'فشل في جلب حالة مخزون المنتجات');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getInventoryStatistics (): Promise<InventoryStatisticsDTO> {
    try {
      // Prevent multiple simultaneous requests to statistics endpoint
      if (InventoryAPIDataSource.isStatisticsRequestInProgress) {

        // Wait for the current request to complete
        while (InventoryAPIDataSource.isStatisticsRequestInProgress) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        // Return cached result if available
        if (InventoryAPIDataSource.lastStatisticsRequest) {
          const timeSinceLastRequest = Date.now() - InventoryAPIDataSource.lastStatisticsRequest.getTime();
          if (timeSinceLastRequest < 30000) { // 30 seconds cache

            return {
              total_products: 0,
              products_with_stock: 0,
              out_of_stock: 0,
              low_stock: 0,
              available_stock: 0,
              total_quantity: 0,
              products_not_in_inventory: 0,
              breakdown: {
                serial_products: 0,
                non_serial_products: 0,
                products_in_inventory: 0
              }
            };
          }
        }
      }

      InventoryAPIDataSource.isStatisticsRequestInProgress = true;
      InventoryAPIDataSource.statisticsRequestCount++;


      const response = await Http.get<BaseResponse<InventoryStatisticsDTO>>(API_ROUTES.INVENTORY.STATISTICS);
      const data = unwrapResponse(response.data, 'فشل في جلب إحصائيات المخزون');
      InventoryAPIDataSource.lastStatisticsRequest = new Date();
      return data;
    } catch (error) {
      handleDataSourceError(error);
    } finally {
      InventoryAPIDataSource.isStatisticsRequestInProgress = false;
    }
  }

  static async transferQuantity (transferData: {
    from_location_id: string;
    to_location_id: string;
    product_id: string;
    quantity: number;
    notes?: string;
  }): Promise<Record<string, unknown>> {
    try {
      const response = await Http.post<BaseResponse<Record<string, unknown>>>(API_ROUTES.INVENTORY.TRANSFER_QUANTITY, transferData);
      return unwrapResponse(response.data, 'فشل في نقل الكمية', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async transferSerial (transferData: {
    serial_id: string;
    from_location_id: string;
    to_location_id: string;
    notes?: string;
  }): Promise<Record<string, unknown>> {
    try {
      const response = await Http.post<BaseResponse<Record<string, unknown>>>(API_ROUTES.INVENTORY.TRANSFER_SERIAL, transferData);
      return unwrapResponse(response.data, 'فشل في نقل الرقم التسلسلي', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getComprehensiveReport (filters?: {
    search?: string;
    has_stock?: boolean;
    low_stock?: boolean;
    product_category?: string;
    product_brand?: string;
    page?: number;
    limit?: number;
  }): Promise<InventoryComprehensiveReport | unknown[]> {
    try {
      const queryParams: Record<string, string | number | boolean> = {};
      if (filters) {
        if (filters.search) {queryParams.search = filters.search;}
        if (filters.has_stock !== undefined) {queryParams.has_stock = filters.has_stock;}
        if (filters.low_stock !== undefined) {queryParams.low_stock = filters.low_stock;}
        if (filters.product_category) {queryParams.product_category = filters.product_category;}
        if (filters.product_brand) {queryParams.product_brand = filters.product_brand;}
        if (filters.page) {queryParams.page = filters.page;}
        if (filters.limit !== undefined) {queryParams.limit = filters.limit;}
      }

      const url = ApiUtils.buildUrl(API_ROUTES.INVENTORY.COMPREHENSIVE_REPORT, queryParams);

      const response = await Http.get<BaseResponse<InventoryComprehensiveReport | unknown[]>>(url);
      return unwrapResponse(response.data, 'فشل في جلب التقرير الشامل');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  static async getSerialStatistics (): Promise<SerialStatisticsDTO> {
    try {
      const response = await Http.get<BaseResponse<SerialStatisticsDTO>>(API_ROUTES.SERIALS.STATISTICS);
      return unwrapResponse(response.data, 'فشل في جلب إحصائيات الأرقام التسلسلية');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
}
